/**
 * Elite Batch 22 Part 10 — Workflow Templates 114–125
 * Marketo B2B: Free trial sales assist, event booth follow-up, ABM engagement,
 * multi-stream engagement, customer onboarding education, customer newsletter,
 * renewal reminders, cross-sell expansion, re-engagement/winback, high-intent
 * sales alert, score decay re-qualification, interactive webinar follow-up.
 *
 * Services pre-selected: Marketo B2B (Salesforce, Zoom, Slack, OpenAI)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function salesforceNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://yourinstance.salesforce.com/services/data/v58.0/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {salesforce_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 22 PART 10 — TEMPLATES 114–125 (Marketo B2B)
// =============================================================================

export const ELITE_BATCH22_PART10: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 114. FREE TRIAL TO SALES ASSIST (Pattern L — Usage/Expansion)
  // ===================================================================

  "Free Trial to Sales Assist": {
    summary:
      "Monitors trial usage spikes in Marketo and routes high-intent trial users to sales for assisted conversion. A webhook fires when a trial user crosses a usage threshold, Salesforce looks up account context and health score, AI analyzes the usage pattern and recommends an action, a condition routes expansion-ready leads to immediate sales alert or education nurture, the appropriate outreach fires, a delay allows follow-up time, a second condition checks conversion status, and a closing email or Slack escalation completes the loop.",
    nodes: [
      trigger("Webhook: Trial usage spike detected in Marketo", "webhook_app", {
        source: "Marketo / Product Analytics",
        event: "trial.usage.spike",
        howItStarts:
          "A Marketo webhook fires when a free trial user exceeds a predefined usage threshold (e.g., 80% of feature limits, high DAU, or API call surge), indicating strong product-market fit and sales readiness",
        setup:
          "Configure Marketo smart campaign to trigger on trial usage milestones. Connect product analytics (Mixpanel, Amplitude, or custom events) to Marketo via webhook. Set threshold values for feature adoption, active days, and API usage.",
        inputFields: [],
      }),

      // 2. Salesforce: Lookup account context and trial details
      salesforceNode(
        "2",
        "Salesforce: Lookup trial account context and health",
        280,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Email", customName: "lead_email", type: "string" },
          { name: "Company", customName: "company_name", type: "string" },
          {
            name: "NumberOfEmployees",
            customName: "company_size",
            type: "number",
          },
          { name: "Industry", customName: "industry", type: "string" },
          {
            name: "Trial_Usage_Score__c",
            customName: "usage_score",
            type: "number",
          },
          {
            name: "Trial_Days_Remaining__c",
            customName: "days_remaining",
            type: "number",
          },
          { name: "OwnerId", customName: "lead_owner_id", type: "string" },
        ],
      ),

      // 3. AI: Analyze usage pattern and recommend action
      aiNode(
        "3",
        "OpenAI: Analyze trial usage and recommend sales action",
        520,
        "You are a B2B SaaS sales intelligence analyst. Given trial usage data, determine whether the lead is expansion-ready (high usage, good fit, nearing limits) or needs education (moderate usage, exploring). Return JSON with intent_level (high or moderate), recommended_action (sales_assist or education_nurture), talking_points (string with 3 key points for the AE), and urgency (immediate or standard).",
        "Trial lead: {lead_email} at {company_name} ({industry}, {company_size} employees). Usage score: {usage_score}/100. Days remaining: {days_remaining}. Webhook payload: {webhook_payload}",
        [
          { field: "intent_level", as: "intent_level", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 4. Condition: Expansion-ready vs education nurture
      n("4", "condition", "Is lead expansion-ready for sales assist?", 760, {
        conditionData: [
          {
            variable: "recommended_action",
            value: "sales_assist",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. True: Slack alert to sales with full context
      slackNode(
        "5",
        "Slack: Alert AE with trial-to-paid opportunity",
        1000,
        "🔥 *Sales Assist Needed — Trial Conversion Opportunity*\n• Lead: {lead_email} at {company_name}\n• Industry: {industry} | Size: {company_size}\n• Usage Score: {usage_score}/100 | Days Left: {days_remaining}\n• Intent: {intent_level} | Urgency: {urgency}\n• Talking Points: {talking_points}\n• Action: Reach out within 24h for assisted demo/upgrade",
      ),

      // 5b. False: Education nurture email
      emailNode(
        "5b",
        "Marketo: Send product education and best practices email",
        1000,
        "{lead_email}",
        "Unlock the full potential of your trial — tips from power users",
        "Hi there,\\n\\nWe noticed you are making great progress with your trial at {company_name}! Here are some power-user tips to help you get even more value:\\n\\n{talking_points}\\n\\nWith {days_remaining} days left, now is the perfect time to explore advanced features. Book a personalized walkthrough with our team to see how {company_name} can scale with a full plan.\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow follow-up time
      n("6", "delayAction", "Wait 5 days for sales follow-up", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: Did the trial convert?
      n("7", "condition", "Has the trial converted to paid?", 1480, {
        conditionData: [
          {
            variable: "usage_score",
            value: "90",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Slack escalation or closing email
      slackNode(
        "8",
        "Slack: Escalate unconverted high-intent trial to sales manager",
        1720,
        "⏰ *Trial Expiring — High-Intent Lead Not Converted*\n• Lead: {lead_email} at {company_name}\n• Usage Score: {usage_score} | Days Left: {days_remaining}\n• Original Recommendation: {recommended_action}\n• Please escalate: trial is expiring with strong usage signals",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 115. EVENT BOOTH LEAD FOLLOW-UP (Pattern I — Content/Event Nurture)
  // ===================================================================

  "Event Booth Lead Follow-Up": {
    summary:
      "Captures event booth scans and delivers fast personalized follow-up with progressive content nurture. A webhook fires when a badge is scanned at the booth, AI extracts lead details and conversation notes, Salesforce looks up the lead lifecycle stage, a condition routes engaged vs. passive leads, the engaged path gets a personalized recap email, a delay allows content consumption, a second condition checks follow-up engagement, and progressive content or a meeting invite closes the loop.",
    nodes: [
      trigger("Webhook: Event booth badge scanned in Marketo", "webhook_app", {
        source: "Marketo / Event Badge Scanner",
        event: "event.booth.badge_scanned",
        howItStarts:
          "A badge scan at the event booth triggers a Marketo webhook containing the scanned lead data and any booth rep notes captured during the conversation",
        setup:
          "Configure your event badge scanning app (e.g., Cvent LeadCapture, iCapture, or custom QR scanner) to POST scanned data to Marketo. Ensure booth reps can add conversation notes and interest tags at scan time.",
        inputFields: [],
      }),

      // 2. AI: Extract lead details and conversation context
      aiNode(
        "2",
        "OpenAI: Extract booth lead details and interest signals",
        280,
        "You are an event marketing specialist. Extract lead details from a booth badge scan payload. Find the email, full name, company, job title, conversation notes, and interest topics regardless of field naming. Classify engagement_level as engaged (had meaningful conversation, asked questions, showed product interest) or passive (quick scan, no conversation). Return JSON with lead_email, lead_name, company, job_title, conversation_notes, interest_topics, engagement_level.",
        "Badge scan payload: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          {
            field: "conversation_notes",
            as: "conversation_notes",
            type: "string",
          },
          { field: "interest_topics", as: "interest_topics", type: "string" },
          { field: "engagement_level", as: "engagement_level", type: "string" },
        ],
      ),

      // 3. Salesforce: Lookup lifecycle stage and prior history
      salesforceNode(
        "3",
        "Salesforce: Lookup lead lifecycle stage and content history",
        520,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Status", customName: "lifecycle_stage", type: "string" },
          { name: "LeadSource", customName: "lead_source", type: "string" },
          {
            name: "Last_Content_Consumed__c",
            customName: "last_content",
            type: "string",
          },
          {
            name: "Has_Open_Opportunity__c",
            customName: "has_opportunity",
            type: "string",
          },
        ],
      ),

      // 4. Condition: Engaged vs passive booth visitor
      n("4", "condition", "Was the booth visitor actively engaged?", 760, {
        conditionData: [
          {
            variable: "engagement_level",
            value: "engaged",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. True: Personalized recap email for engaged leads
      emailNode(
        "5",
        "Marketo: Send personalized booth recap with relevant resources",
        1000,
        "{lead_email}",
        "Great meeting you at the booth, {lead_name}!",
        "Hi {lead_name},\\n\\nIt was great connecting with you at the booth! Based on our conversation about {interest_topics}, I wanted to share some resources that are directly relevant to what {company} is working on:\\n\\n• Product deep-dive on {interest_topics}\\n• Customer case study in your space\\n• Technical architecture guide\\n\\nNotes from our chat: {conversation_notes}\\n\\nWould you like to schedule a more detailed walkthrough? I can set up 30 minutes with a solutions engineer who specializes in your area.\\n\\nBest regards,\\nThe Team",
      ),

      // 5b. False: Generic follow-up for passive scans
      emailNode(
        "5b",
        "Marketo: Send general event follow-up with top resources",
        1000,
        "{lead_email}",
        "Thanks for stopping by our booth!",
        "Hi {lead_name},\\n\\nThanks for visiting our booth at the event! Here are some of our most popular resources that attendees found valuable:\\n\\n• Product overview and demo video\\n• Industry trends report\\n• Getting started guide\\n\\nFeel free to explore at your own pace, and reply if you have any questions.\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow content consumption
      n("6", "delayAction", "Wait 4 days for content consumption", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 7. Condition: Did the lead engage with follow-up content?
      n(
        "7",
        "condition",
        "Has the lead opened or clicked the follow-up email?",
        1480,
        {
          conditionData: [
            {
              variable: "engagement_level",
              value: "engaged",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Progressive content: Case study or meeting invite
      emailNode(
        "8",
        "Marketo: Send case study and meeting invite for engaged leads",
        1720,
        "{lead_email}",
        "{lead_name}, see how {company}-like teams achieved 3x ROI",
        "Hi {lead_name},\\n\\nSince you showed interest in {interest_topics}, I thought you would find this case study compelling — a company similar to {company} achieved 3x ROI within 6 months of implementation.\\n\\n📄 [Read the full case study]\\n\\nReady to explore what this could look like for {company}? Book a personalized demo below:\\n\\n[Schedule a 30-minute demo →]\\n\\nLooking forward to continuing our conversation from the event.\\n\\nBest,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 116. ABM ENGAGEMENT PROGRAM (Pattern I — Content/Event Nurture)
  // ===================================================================

  "ABM Engagement Program": {
    summary:
      "Delivers personalized account-based marketing content when target accounts show engagement signals. A webhook fires when a target account member engages with content, AI extracts the engagement details and account context, Salesforce looks up account tier and opportunity status, a condition routes high-engagement accounts to personalized content vs. standard nurture, the appropriate email sends, a delay allows engagement time, a second condition checks for deeper engagement, and a meeting push or continued nurture completes the cycle.",
    nodes: [
      trigger(
        "Webhook: Target account engagement detected in Marketo",
        "webhook_app",
        {
          source: "Marketo / 6sense / Demandbase",
          event: "abm.account.engagement",
          howItStarts:
            "A Marketo webhook fires when a member of a target ABM account engages with content (website visit, content download, ad click, or email engagement), indicating account-level interest acceleration",
          setup:
            "Configure Marketo smart campaign with ABM filters (target account list, 6sense/Demandbase intent data). Set engagement thresholds for account-level triggers. Connect ABM platform intent signals to Marketo via API.",
          inputFields: [],
        },
      ),

      // 2. AI: Extract engagement details and account context
      aiNode(
        "2",
        "OpenAI: Extract ABM engagement signal and account context",
        280,
        "You are an ABM strategist. Extract engagement details from the webhook payload. Find the contact email, name, company/account name, engagement type (content download, website visit, ad click, email open), content topic, and engagement score. Determine account_heat as hot (multiple touches, high-value content) or warm (single touch, awareness content). Return JSON with contact_email, contact_name, account_name, engagement_type, content_topic, engagement_score, account_heat.",
        "ABM engagement payload: {webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "engagement_type", as: "engagement_type", type: "string" },
          { field: "content_topic", as: "content_topic", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
          { field: "account_heat", as: "account_heat", type: "string" },
        ],
      ),

      // 3. Salesforce: Lookup account tier and opportunity status
      salesforceNode(
        "3",
        "Salesforce: Lookup ABM account tier and opportunity pipeline",
        520,
        "GET",
        "sobjects/Account/{account_id}",
        [],
        [
          { name: "ABM_Tier__c", customName: "abm_tier", type: "string" },
          {
            name: "Open_Opportunities__c",
            customName: "open_opportunities",
            type: "number",
          },
          { name: "Industry", customName: "account_industry", type: "string" },
          {
            name: "AnnualRevenue",
            customName: "annual_revenue",
            type: "number",
          },
          { name: "OwnerId", customName: "account_owner_id", type: "string" },
        ],
      ),

      // 4. Condition: Hot account vs warm nurture
      n(
        "4",
        "condition",
        "Is this a hot ABM account with strong engagement?",
        760,
        {
          conditionData: [
            {
              variable: "account_heat",
              value: "hot",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Personalized ABM content for hot accounts
      emailNode(
        "5",
        "Marketo: Send personalized ABM content for hot account",
        1000,
        "{contact_email}",
        "{contact_name}, a custom perspective for {account_name}",
        "Hi {contact_name},\\n\\nWe have been thinking about the challenges facing {account_industry} companies like {account_name}, particularly around {content_topic}.\\n\\nWe put together a personalized perspective that maps our solution to your specific use case:\\n\\n📊 [Custom ROI Analysis for {account_name}]\\n📄 [Industry Case Study: {account_industry}]\\n🎥 [3-minute demo focused on {content_topic}]\\n\\nWould it be valuable to discuss how teams at companies similar to {account_name} are approaching this? I can arrange a 20-minute executive briefing.\\n\\nBest regards,\\nThe ABM Team",
      ),

      // 5b. False: Standard nurture for warm accounts
      emailNode(
        "5b",
        "Marketo: Send relevant content nurture for warm account",
        1000,
        "{contact_email}",
        "Resources on {content_topic} that {account_name} teams find valuable",
        "Hi {contact_name},\\n\\nBased on your recent interest in {content_topic}, here are some resources that teams in {account_industry} have found especially useful:\\n\\n• Trends report: The state of {content_topic} in {account_industry}\\n• Best practices guide from industry leaders\\n• On-demand webinar with expert panel\\n\\nExplore at your own pace, and feel free to reach out with questions.\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow engagement time
      n("6", "delayAction", "Wait 5 days for content engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: Deeper engagement after content delivery?
      n(
        "7",
        "condition",
        "Has the contact engaged with ABM content (clicked or revisited)?",
        1480,
        {
          conditionData: [
            {
              variable: "engagement_score",
              value: "50",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Progressive: Meeting push or continued nurture
      emailNode(
        "8",
        "Marketo: Send executive briefing invite for engaged ABM contact",
        1720,
        "{contact_email}",
        "Let us set up a quick call, {contact_name}",
        "Hi {contact_name},\\n\\nI noticed {account_name} has been actively exploring solutions around {content_topic}. Our executive team has prepared a brief, no-pressure session specifically for {account_industry} leaders:\\n\\n🗓 [Book a 20-min Executive Briefing →]\\n\\nWe will cover:\\n• How similar {account_industry} companies solved {content_topic} challenges\\n• Estimated ROI based on {account_name} profile\\n• Implementation timeline and quick wins\\n\\nNo commitment required — just a focused conversation.\\n\\nBest,\\nThe ABM Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 117. MULTI-STREAM ENGAGEMENT PROGRAM (Pattern I — Content/Event Nurture)
  // ===================================================================

  "Multi-Stream Engagement Program": {
    summary:
      "Routes leads into topic-matched content streams and delivers progressive nurture based on interest signals. A webhook fires when a lead signals a topic interest, AI extracts the topic and engagement details, Salesforce looks up lifecycle stage and content history, a condition routes to the matched stream vs. default track, the stream-appropriate email sends, a delay allows consumption, a second condition checks engagement depth, and progressive content or stream advancement completes the cycle.",
    nodes: [
      trigger(
        "Webhook: Lead topic interest detected in Marketo",
        "webhook_app",
        {
          source: "Marketo / Website / Content Platform",
          event: "lead.topic_interest.detected",
          howItStarts:
            "A Marketo webhook fires when a lead signals topic interest through content download, page visits, or form selection (e.g., selecting a product area or use-case on a form), routing them into the appropriate engagement stream",
          setup:
            "Configure Marketo engagement program with multiple streams (e.g., Stream A: Security, Stream B: Analytics, Stream C: Integration, Stream D: Compliance). Set transition rules between streams based on content exhaustion or interest shift.",
          inputFields: [],
        },
      ),

      // 2. AI: Extract topic interest and stream assignment
      aiNode(
        "2",
        "OpenAI: Identify topic interest and assign content stream",
        280,
        "You are a demand-gen strategist. Analyze the lead engagement payload and determine the best content stream. Identify the email, name, company, the primary topic of interest, and assign a stream_id from: security, analytics, integration, compliance, or general. Determine engagement_depth as deep (multiple content pieces, long dwell time) or surface (single touch, quick browse). Return JSON with lead_email, lead_name, company, topic_interest, stream_id, engagement_depth, content_stage (awareness, consideration, or decision).",
        "Topic interest payload: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "topic_interest", as: "topic_interest", type: "string" },
          { field: "stream_id", as: "stream_id", type: "string" },
          { field: "engagement_depth", as: "engagement_depth", type: "string" },
          { field: "content_stage", as: "content_stage", type: "string" },
        ],
      ),

      // 3. Salesforce: Lookup lifecycle stage and content history
      salesforceNode(
        "3",
        "Salesforce: Lookup lead lifecycle stage and content consumption",
        520,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Status", customName: "lifecycle_stage", type: "string" },
          {
            name: "Content_Stream__c",
            customName: "current_stream",
            type: "string",
          },
          {
            name: "Content_Pieces_Consumed__c",
            customName: "content_consumed",
            type: "number",
          },
          {
            name: "Last_Content_Date__c",
            customName: "last_content_date",
            type: "string",
          },
        ],
      ),

      // 4. Condition: Deep engagement vs surface
      n(
        "4",
        "condition",
        "Does the lead have deep engagement (consideration+ stage)?",
        760,
        {
          conditionData: [
            {
              variable: "engagement_depth",
              value: "deep",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Stream-matched consideration content
      emailNode(
        "5",
        "Marketo: Send stream-matched consideration content",
        1000,
        "{lead_email}",
        "{lead_name}, a deep dive on {topic_interest} for {company}",
        "Hi {lead_name},\\n\\nSince you have been exploring {topic_interest} content, here is a curated deep-dive package for your current stage:\\n\\n📄 Technical white paper: Solving {topic_interest} at enterprise scale\\n🎥 Customer story: How a company like {company} approached {topic_interest}\\n📊 Comparison guide: Evaluating {topic_interest} solutions\\n\\nThese resources are specifically selected for the {stream_id} track. You have consumed {content_consumed} pieces so far — here is what comes next.\\n\\nBest,\\nThe Content Team",
      ),

      // 5b. False: Awareness-level stream content
      emailNode(
        "5b",
        "Marketo: Send awareness content for matched stream",
        1000,
        "{lead_email}",
        "Get started with {topic_interest} — curated for you",
        "Hi {lead_name},\\n\\nWelcome to our {stream_id} content track! Based on your interest in {topic_interest}, here are some foundational resources to get you started:\\n\\n• Introduction to {topic_interest}: What you need to know\\n• Industry trends in {stream_id} for 2024\\n• Quick-start guide to evaluating solutions\\n\\nWe will send you relevant content at a comfortable pace. Reply anytime to adjust your preferences.\\n\\nBest,\\nThe Content Team",
        380,
      ),

      // 6. Delay: Allow content consumption
      n("6", "delayAction", "Wait 5 days for content engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: Consumed follow-up content?
      n(
        "7",
        "condition",
        "Has the lead consumed the stream content (clicked or downloaded)?",
        1480,
        {
          conditionData: [
            {
              variable: "content_consumed",
              value: "3",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Progressive: Next-level content or evaluation offer
      emailNode(
        "8",
        "Marketo: Send progressive content and evaluation offer",
        1720,
        "{lead_email}",
        "Ready for the next step, {lead_name}? ROI calculator inside",
        "Hi {lead_name},\\n\\nYou are making great progress in our {stream_id} track! Based on your engagement, here is the next level:\\n\\n🔢 [Interactive ROI Calculator for {topic_interest}]\\n📞 [Book a 15-min specialist Q&A on {topic_interest}]\\n📋 [Custom evaluation checklist for {company}]\\n\\nOver {content_consumed} resources explored — you are well on your way to a well-informed decision.\\n\\nBest,\\nThe Content Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 118. CUSTOMER ONBOARDING EDUCATION (Pattern E — Post-Purchase)
  // ===================================================================

  "Customer Onboarding Education": {
    summary:
      "Delivers structured onboarding education to new customers and tracks adoption milestones. A webhook fires when a new customer deal closes in Marketo, AI extracts the customer and product details, Salesforce looks up customer tenure and account type, a condition routes first-time customers to comprehensive onboarding vs. returning customers to quick-start, the appropriate education email sends, a delay allows implementation time, a second condition checks product adoption, and a review/next-steps email completes onboarding.",
    nodes: [
      trigger("Webhook: New customer closed-won in Marketo", "webhook_app", {
        source: "Marketo / Salesforce",
        event: "customer.deal.closed_won",
        howItStarts:
          "A Marketo webhook fires when a deal is marked closed-won in Salesforce, initiating the customer onboarding education sequence with product-specific training content and milestone tracking",
        setup:
          "Configure Marketo smart campaign to trigger on Salesforce opportunity stage change to Closed Won. Map product line, contract tier, and CSM assignment fields. Prepare onboarding content library by product and tier.",
        inputFields: [],
      }),

      // 2. AI: Extract customer and product details
      aiNode(
        "2",
        "OpenAI: Extract new customer details and product configuration",
        280,
        "You are a customer success onboarding specialist. Extract the new customer details from the closed-won webhook payload. Find the primary contact email, name, company, product purchased, contract tier (starter, professional, enterprise), and implementation timeline. Determine if this is a first_purchase (new logo) or expansion (existing customer buying more). Return JSON with customer_email, customer_name, company, product_purchased, contract_tier, implementation_timeline, purchase_type (first_purchase or expansion).",
        "Closed-won payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          {
            field: "product_purchased",
            as: "product_purchased",
            type: "string",
          },
          { field: "contract_tier", as: "contract_tier", type: "string" },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
          { field: "purchase_type", as: "purchase_type", type: "string" },
        ],
      ),

      // 3. Salesforce: Lookup customer tenure and VIP status
      salesforceNode(
        "3",
        "Salesforce: Lookup customer tenure and account details",
        520,
        "GET",
        "sobjects/Account/{account_id}",
        [],
        [
          {
            name: "Customer_Since__c",
            customName: "customer_since",
            type: "string",
          },
          {
            name: "Account_Tier__c",
            customName: "account_tier",
            type: "string",
          },
          { name: "CSM_Name__c", customName: "csm_name", type: "string" },
          { name: "CSM_Email__c", customName: "csm_email", type: "string" },
          {
            name: "Products_Owned__c",
            customName: "products_owned",
            type: "string",
          },
        ],
      ),

      // 4. Condition: First purchase (new logo)?
      n("4", "condition", "Is this a first-time customer (new logo)?", 760, {
        conditionData: [
          {
            variable: "purchase_type",
            value: "first_purchase",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. True: Comprehensive onboarding for new customers
      emailNode(
        "5",
        "Marketo: Send comprehensive onboarding welcome and setup guide",
        1000,
        "{customer_email}",
        "Welcome to the family, {customer_name}! Your onboarding roadmap is here",
        "Hi {customer_name},\\n\\nWelcome aboard! We are thrilled to have {company} as a customer. Your dedicated CSM, {csm_name}, will be your guide through onboarding.\\n\\nHere is your {contract_tier}-tier onboarding roadmap for {product_purchased}:\\n\\n✅ Week 1: Platform setup and admin configuration\\n✅ Week 2: Team onboarding and training sessions\\n✅ Week 3: Data migration and integration setup\\n✅ Week 4: Go-live checklist and first campaign\\n\\n📚 [Access your onboarding portal →]\\n📅 [Schedule your kickoff call with {csm_name} →]\\n📖 [Download the {product_purchased} quickstart guide →]\\n\\nTimeline: {implementation_timeline}\\n\\nLet us get you up and running!\\n{csm_name}",
      ),

      // 5b. False: Quick-start for expansion purchases
      emailNode(
        "5b",
        "Marketo: Send expansion quick-start guide for existing customer",
        1000,
        "{customer_email}",
        "Your new {product_purchased} module is ready, {customer_name}!",
        "Hi {customer_name},\\n\\nGreat news — {product_purchased} is now active on your {company} account! Since you are already familiar with the platform, here is a streamlined setup guide:\\n\\n🚀 [Quick-start: Activate {product_purchased} →]\\n🔗 [Integration guide with your existing {products_owned} →]\\n📹 [5-min feature overview video →]\\n\\nYour CSM {csm_name} is available for any questions.\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow implementation time
      n("6", "delayAction", "Wait 10 days for implementation progress", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),

      // 7. Condition: Has the customer activated the product?
      n(
        "7",
        "condition",
        "Has the customer completed initial product setup?",
        1480,
        {
          conditionData: [
            {
              variable: "purchase_type",
              value: "first_purchase",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Adoption check email or advanced tips
      emailNode(
        "8",
        "Marketo: Send adoption check-in and advanced tips",
        1720,
        "{customer_email}",
        "How is onboarding going, {customer_name}? Here are your next milestones",
        "Hi {customer_name},\\n\\nYou are 10 days into your {product_purchased} journey at {company}! Here is a quick check-in:\\n\\n📊 Your onboarding progress and next milestones\\n💡 Advanced tips from power users in {contract_tier} tier\\n🎓 [Register for the advanced training webinar →]\\n📞 [Book a check-in with {csm_name} →]\\n\\nWe want to make sure you are getting maximum value. Reply with any questions or roadblocks — we are here to help.\\n\\nBest,\\n{csm_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 119. CUSTOMER NEWSLETTER STREAM (Pattern I — Content/Event Nurture)
  // ===================================================================

  "Customer Newsletter Stream": {
    summary:
      "Delivers personalized monthly newsletter content to customers and tracks engagement for progressive nurture. A webhook fires when the customer newsletter is triggered, AI analyzes the subscriber profile and selects relevant content, Salesforce looks up product usage and content preferences, a condition routes engaged vs. passive subscribers, the engaged path gets a curated digest, a delay allows reading time, a second condition checks open/click behavior, and a deeper resource or re-engagement content follows.",
    nodes: [
      trigger(
        "Webhook: Customer newsletter cycle triggered in Marketo",
        "webhook_app",
        {
          source: "Marketo / Customer Platform",
          event: "newsletter.customer.triggered",
          howItStarts:
            "A Marketo webhook fires at the start of each newsletter cycle, delivering a personalized customer digest with product updates, tips, and curated content based on the subscriber product usage and engagement history",
          setup:
            "Configure Marketo engagement program to trigger monthly newsletter sends. Segment customers by product owned, usage tier, and engagement level. Prepare a content library with product updates, tips, case studies, and community highlights.",
          inputFields: [],
        },
      ),

      // 2. AI: Analyze subscriber profile and curate content
      aiNode(
        "2",
        "OpenAI: Curate personalized newsletter content for subscriber",
        280,
        "You are a customer marketing content curator. Given the subscriber webhook data, extract the customer email, name, company, and determine their primary product interest. Select 3 newsletter_highlights (comma-separated) that would be most relevant. Classify engagement_history as active (regularly opens/clicks) or passive (rarely engages). Return JSON with subscriber_email, subscriber_name, company, primary_product, newsletter_highlights, engagement_history.",
        "Newsletter subscriber data: {webhook_payload}",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "primary_product", as: "primary_product", type: "string" },
          {
            field: "newsletter_highlights",
            as: "newsletter_highlights",
            type: "string",
          },
          {
            field: "engagement_history",
            as: "engagement_history",
            type: "string",
          },
        ],
      ),

      // 3. Salesforce: Lookup product usage and content preferences
      salesforceNode(
        "3",
        "Salesforce: Lookup customer product usage and preferences",
        520,
        "GET",
        "sobjects/Contact/{contact_id}",
        [],
        [
          {
            name: "Product_Usage_Tier__c",
            customName: "usage_tier",
            type: "string",
          },
          {
            name: "Newsletter_Preference__c",
            customName: "content_preference",
            type: "string",
          },
          {
            name: "Last_Email_Engagement__c",
            customName: "last_engagement_date",
            type: "string",
          },
          {
            name: "Feature_Adoption_Score__c",
            customName: "adoption_score",
            type: "number",
          },
        ],
      ),

      // 4. Condition: Engaged subscriber vs passive
      n("4", "condition", "Is the subscriber an active engager?", 760, {
        conditionData: [
          {
            variable: "engagement_history",
            value: "active",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. True: Rich curated digest for engaged subscribers
      emailNode(
        "5",
        "Marketo: Send curated newsletter digest for engaged customer",
        1000,
        "{subscriber_email}",
        "{subscriber_name}, your monthly {primary_product} digest is here",
        "Hi {subscriber_name},\\n\\nHere is your personalized monthly digest curated for {company}:\\n\\n🔥 This Month Highlights:\\n{newsletter_highlights}\\n\\n📈 Your {primary_product} Usage Snapshot:\\n• Adoption score: {adoption_score}\\n• Usage tier: {usage_tier}\\n• New features you have not tried yet\\n\\n💡 Power User Tips:\\n• Advanced workflow for {primary_product}\\n• Integration spotlight: Connect with your existing tools\\n• Community Q&A highlights\\n\\n📅 Upcoming Events:\\n• Monthly product webinar\\n• User group meetup\\n\\nHappy reading!\\nThe Customer Team",
      ),

      // 5b. False: Simplified digest for passive subscribers
      emailNode(
        "5b",
        "Marketo: Send simplified newsletter for passive subscriber",
        1000,
        "{subscriber_email}",
        "Quick update from us, {subscriber_name}",
        "Hi {subscriber_name},\\n\\nHere is a quick summary of what is new this month:\\n\\n📌 Top 3 Things to Know:\\n{newsletter_highlights}\\n\\n🚀 One Quick Win:\\nTry this {primary_product} tip that takes 2 minutes and saves hours.\\n\\nQuestions? Reply to this email — we read every response.\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow reading time
      n("6", "delayAction", "Wait 3 days for newsletter engagement", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 7. Condition: Did the subscriber engage with the newsletter?
      n(
        "7",
        "condition",
        "Did the subscriber open and click the newsletter?",
        1480,
        {
          conditionData: [
            {
              variable: "adoption_score",
              value: "60",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Progressive: Deeper resource or re-engagement
      emailNode(
        "8",
        "Marketo: Send deeper resource based on newsletter clicks",
        1720,
        "{subscriber_email}",
        "Since you liked {newsletter_highlights} — here is more, {subscriber_name}",
        "Hi {subscriber_name},\\n\\nWe noticed you were interested in our newsletter content! Here is a deeper dive:\\n\\n📄 [In-depth guide on {newsletter_highlights}]\\n🎥 [On-demand masterclass for {primary_product}]\\n👥 [Join the community discussion]\\n\\nWant to shape future content? Take our 1-minute preference survey.\\n\\nBest,\\nThe Customer Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 120. RENEWAL REMINDER STREAM (Pattern F — Renewal/Subscription)
  // ===================================================================

  "Renewal Reminder Stream": {
    summary:
      "Sends a tiered renewal reminder sequence (90/60/30 days) and escalates at-risk renewals to the CSM team. A scheduled trigger checks for upcoming renewals, AI extracts subscription details and renewal context, Salesforce checks payment status and account health, a condition routes at-risk renewals vs. healthy renewals, the appropriate reminder email sends, a delay allows response time, a second condition checks whether the renewal is still unresolved, and a stronger reminder with value recap or CSM escalation closes the loop.",
    nodes: [
      trigger("Schedule: Check for upcoming renewals in Marketo", "schedule", {
        schedule: "0 8 * * 1 (every Monday at 8am)",
        howItStarts:
          "A scheduled Marketo smart campaign checks for customers with renewals approaching within 90, 60, or 30 days and triggers the appropriate reminder tier based on urgency and account health",
        setup:
          "Configure Marketo smart campaign with Salesforce sync to pull renewal dates. Set up 3 tiers: 90-day (awareness), 60-day (consideration), 30-day (urgency). Integrate with Salesforce renewal opportunity data and payment status.",
        inputFields: [],
      }),

      // 2. AI: Analyze renewal context and risk
      aiNode(
        "2",
        "OpenAI: Analyze renewal context and determine risk level",
        280,
        "You are a customer success renewal analyst. Given the upcoming renewal data, analyze the account health and renewal risk. Return JSON with customer_email, customer_name, company, renewal_date, days_until_renewal (number), contract_value, renewal_tier (90day, 60day, or 30day), risk_level (healthy or at_risk), risk_factors (string describing any concerns), and recommended_tone (celebratory, informational, or urgent).",
        "Renewal data: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          {
            field: "days_until_renewal",
            as: "days_until_renewal",
            type: "number",
          },
          { field: "contract_value", as: "contract_value", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
        ],
      ),

      // 3. Salesforce: Check payment status and account health
      salesforceNode(
        "3",
        "Salesforce: Check renewal opportunity and payment health",
        520,
        "GET",
        "sobjects/Opportunity/{opportunity_id}",
        [],
        [
          { name: "StageName", customName: "renewal_stage", type: "string" },
          { name: "Amount", customName: "renewal_amount", type: "number" },
          {
            name: "Health_Score__c",
            customName: "health_score",
            type: "number",
          },
          { name: "CSM_Email__c", customName: "csm_email", type: "string" },
          {
            name: "Last_Payment_Status__c",
            customName: "payment_status",
            type: "string",
          },
        ],
      ),

      // 4. Condition: At-risk renewal?
      n("4", "condition", "Is this renewal at risk (health score < 70)?", 760, {
        conditionData: [
          {
            variable: "risk_level",
            value: "at_risk",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. True: Personalized renewal reminder with value recap for at-risk
      emailNode(
        "5",
        "Marketo: Send renewal reminder with value recap for at-risk customer",
        1000,
        "{customer_email}",
        "{customer_name}, your renewal is coming up — let us make sure you are getting full value",
        "Hi {customer_name},\\n\\nYour {company} subscription renews on {renewal_date} ({days_until_renewal} days away).\\n\\nBefore renewal, we want to make sure you are getting maximum value. Here is a snapshot of what you have achieved:\\n\\n📊 Your Value Dashboard:\\n• Contract: {contract_value}\\n• Health Score: {health_score}/100\\n• Key wins and milestones this period\\n\\nWe noticed some areas where you could be getting more value. Your CSM {csm_email} would love to schedule a quick review to:\\n\\n✅ Address any concerns: {risk_factors}\\n✅ Optimize your configuration\\n✅ Explore features you have not tried yet\\n\\n📞 [Schedule a renewal review call →]\\n\\nWe are committed to your success.\\nThe Customer Team",
      ),

      // 5b. False: Standard renewal reminder for healthy accounts
      emailNode(
        "5b",
        "Marketo: Send standard renewal reminder for healthy customer",
        1000,
        "{customer_email}",
        "{customer_name}, your renewal is approaching — here is what is new",
        "Hi {customer_name},\\n\\nJust a heads-up: your {company} subscription renews on {renewal_date} ({days_until_renewal} days away).\\n\\nHere is what has happened since your last renewal:\\n\\n🆕 New features added to your plan\\n📈 Your usage highlights and ROI\\n🎯 Upcoming roadmap items you will love\\n\\nNo action needed if you would like to continue — your renewal will process automatically.\\n\\nQuestions? Reply here or reach out to your CSM.\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow response time
      n("6", "delayAction", "Wait 5 days for renewal response", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: Still unresolved?
      n(
        "7",
        "condition",
        "Is the renewal still unresolved with less than 30 days left?",
        1480,
        {
          conditionData: [
            {
              variable: "days_until_renewal",
              value: "30",
              comparison_operator: "LESS THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Escalation: Stronger reminder or CSM alert
      emailNode(
        "8",
        "Marketo: Send urgent renewal escalation with CSM involvement",
        1720,
        "{customer_email}",
        "Important: Your {company} renewal needs attention, {customer_name}",
        "Hi {customer_name},\\n\\nYour subscription renewal on {renewal_date} is approaching quickly and we want to ensure a smooth process.\\n\\n⚠️ Action needed: Please review your renewal details and confirm any changes.\\n\\nRenewal Summary:\\n• Amount: {contract_value}\\n• Renewal Date: {renewal_date}\\n• Account Health: {health_score}/100\\n\\nYour CSM {csm_email} is standing by to help with any questions, adjustments, or concerns about your renewal.\\n\\n📞 [Speak with your CSM today →]\\n📝 [Review and confirm renewal →]\\n\\nWe value {company} as a customer and want to continue partnering with you.\\n\\nBest,\\nThe Customer Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 121. CROSS-SELL EXPANSION NURTURE (Pattern L — Usage/Expansion)
  // ===================================================================

  "Cross-Sell Expansion Nurture": {
    summary:
      "Identifies cross-sell opportunities from product usage patterns and nurtures expansion with targeted content. A webhook fires when a usage threshold indicates expansion readiness, Salesforce looks up account context and products owned, AI analyzes usage and recommends expansion products, a condition routes expansion-ready vs. save-risk accounts, the expansion path sends targeted content, a delay allows consideration, a second condition checks engagement with expansion content, and a sales assist alert or continued nurture follows.",
    nodes: [
      trigger(
        "Webhook: Product usage threshold crossed in Marketo",
        "webhook_app",
        {
          source: "Marketo / Product Analytics",
          event: "usage.threshold.crossed",
          howItStarts:
            "A Marketo webhook fires when a customer crosses a product usage threshold that signals expansion opportunity (e.g., hitting API limits, inviting many users, exploring advanced features), triggering cross-sell nurture",
          setup:
            "Configure Marketo smart campaign to trigger on product usage milestones. Integrate product analytics to track feature usage, user count, API calls, and storage consumption. Map usage patterns to expansion product recommendations.",
          inputFields: [],
        },
      ),

      // 2. Salesforce: Lookup account context and products owned
      salesforceNode(
        "2",
        "Salesforce: Lookup account products owned and expansion history",
        280,
        "GET",
        "sobjects/Account/{account_id}",
        [],
        [
          { name: "Name", customName: "account_name", type: "string" },
          {
            name: "Products_Owned__c",
            customName: "products_owned",
            type: "string",
          },
          {
            name: "Contract_Tier__c",
            customName: "current_tier",
            type: "string",
          },
          {
            name: "Health_Score__c",
            customName: "health_score",
            type: "number",
          },
          {
            name: "AnnualRevenue",
            customName: "annual_revenue",
            type: "number",
          },
          { name: "OwnerId", customName: "account_owner_id", type: "string" },
          {
            name: "Primary_Contact_Email__c",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "Primary_Contact_Name__c",
            customName: "contact_name",
            type: "string",
          },
        ],
      ),

      // 3. AI: Analyze usage and recommend expansion
      aiNode(
        "3",
        "OpenAI: Analyze usage pattern and recommend expansion products",
        520,
        "You are a B2B expansion strategist. Given the customer usage data and products owned, recommend the best cross-sell/upsell product. Determine if the account is expansion_ready (high usage, healthy account, clear need) or save_risk (declining usage, low health). Return JSON with recommended_product, expansion_reason, value_proposition, intent_signal (expansion or save_risk), and urgency (high or moderate).",
        "Account: {account_name}, Products owned: {products_owned}, Tier: {current_tier}, Health: {health_score}/100, Revenue: {annual_revenue}. Usage signal: {webhook_payload}",
        [
          {
            field: "recommended_product",
            as: "recommended_product",
            type: "string",
          },
          { field: "expansion_reason", as: "expansion_reason", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "intent_signal", as: "intent_signal", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 4. Condition: Expansion-ready vs save-risk
      n(
        "4",
        "condition",
        "Is the account expansion-ready (not save-risk)?",
        760,
        {
          conditionData: [
            {
              variable: "intent_signal",
              value: "expansion",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Expansion content email
      emailNode(
        "5",
        "Marketo: Send cross-sell expansion content with ROI data",
        1000,
        "{contact_email}",
        "{contact_name}, {account_name} is ready for the next level",
        "Hi {contact_name},\\n\\nBased on how {account_name} is using {products_owned}, we think {recommended_product} would be a natural next step.\\n\\nWhy {recommended_product}?\\n{expansion_reason}\\n\\n💰 Value Proposition:\\n{value_proposition}\\n\\n📊 What customers like {account_name} achieve with {recommended_product}:\\n• 40% efficiency improvement on average\\n• Seamless integration with your existing {products_owned} setup\\n• ROI typically realized within 60 days\\n\\n📄 [Read the {recommended_product} product brief →]\\n📞 [Schedule an expansion review →]\\n\\nBest,\\nThe Growth Team",
      ),

      // 5b. False: Internal save alert
      slackNode(
        "5b",
        "Slack: Alert CSM about at-risk account needing intervention",
        1000,
        "⚠️ *At-Risk Account — Save Play Needed*\n• Account: {account_name}\n• Contact: {contact_name} ({contact_email})\n• Health Score: {health_score}/100\n• Products: {products_owned} ({current_tier})\n• Signal: Usage threshold crossed but account shows save-risk indicators\n• Action: CSM outreach recommended for retention play",
        380,
      ),

      // 6. Delay: Allow consideration time
      n(
        "6",
        "delayAction",
        "Wait 7 days for expansion content engagement",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),

      // 7. Condition: Engaged with expansion content?
      n(
        "7",
        "condition",
        "Has the contact engaged with expansion content?",
        1480,
        {
          conditionData: [
            {
              variable: "health_score",
              value: "75",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Sales assist alert or continued nurture
      slackNode(
        "8",
        "Slack: Alert sales for expansion-ready account follow-up",
        1720,
        "📈 *Expansion Opportunity — Sales Assist Ready*\n• Account: {account_name} ({contact_name})\n• Current: {products_owned} ({current_tier})\n• Recommended: {recommended_product}\n• Reason: {expansion_reason}\n• Urgency: {urgency}\n• Contact engaged with expansion content — ready for AE outreach",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 122. RE-ENGAGEMENT / WINBACK (Pattern G — Re-engagement)
  // ===================================================================

  "Re-Engagement / Winback": {
    summary:
      "Re-engages inactive Marketo leads who have crossed the 90-day inactivity threshold with a two-stage winback sequence. A scheduled trigger identifies 90-day inactive leads, Salesforce looks up last activity and purchase/engagement history, AI analyzes the likely inactivity reason and recommends an approach, a condition routes high-value vs. standard leads, the appropriate winback email sends, a delay allows re-engagement, a second condition checks if the lead is still inactive, and a sunset prompt or stronger offer closes the sequence.",
    nodes: [
      trigger(
        "Schedule: Check for 90-day inactive leads in Marketo",
        "schedule",
        {
          schedule: "0 9 * * 1 (every Monday at 9am)",
          howItStarts:
            "A scheduled Marketo smart campaign identifies leads who have had no email opens, clicks, website visits, or form fills in the past 90 days, triggering a structured winback sequence to re-engage or sunset the contact",
          setup:
            "Configure Marketo smart list to filter leads with last activity date > 90 days ago. Exclude recently purchased, do-not-contact, and suppressed contacts. Set up lead scoring adjustments for re-engaged leads.",
          inputFields: [],
        },
      ),

      // 2. Salesforce: Lookup last activity and engagement history
      salesforceNode(
        "2",
        "Salesforce: Lookup lead last activity and lifetime value",
        280,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Email", customName: "lead_email", type: "string" },
          { name: "FirstName", customName: "lead_name", type: "string" },
          { name: "Company", customName: "company", type: "string" },
          {
            name: "LastActivityDate",
            customName: "last_activity_date",
            type: "string",
          },
          { name: "Lead_Score__c", customName: "lead_score", type: "number" },
          {
            name: "Lifetime_Value__c",
            customName: "lifetime_value",
            type: "number",
          },
          { name: "Industry", customName: "industry", type: "string" },
        ],
      ),

      // 3. AI: Analyze inactivity reason and recommend approach
      aiNode(
        "3",
        "OpenAI: Analyze inactivity reason and recommend winback approach",
        520,
        "You are a re-engagement strategist. Analyze the inactive lead data and determine the most likely inactivity reason: budget_constraint, role_change, competitor_switch, content_fatigue, or timing_mismatch. Classify the lead as high_value (score > 50 or lifetime value > 1000) or standard. Recommend a winback_angle: new_features, fresh_content, special_offer, or preference_reset. Return JSON with inactivity_reason, value_tier (high_value or standard), winback_angle, personalized_hook (one sentence tailored to the lead).",
        "Lead: {lead_email} at {company} ({industry}). Score: {lead_score}, LTV: {lifetime_value}. Last activity: {last_activity_date}.",
        [
          {
            field: "inactivity_reason",
            as: "inactivity_reason",
            type: "string",
          },
          { field: "value_tier", as: "value_tier", type: "string" },
          { field: "winback_angle", as: "winback_angle", type: "string" },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
        ],
      ),

      // 4. Condition: High-value lead?
      n(
        "4",
        "condition",
        "Is this a high-value lead worth premium winback?",
        760,
        {
          conditionData: [
            {
              variable: "value_tier",
              value: "high_value",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Premium winback for high-value leads
      emailNode(
        "5",
        "Marketo: Send premium winback email for high-value lead",
        1000,
        "{lead_email}",
        "We miss you, {lead_name} — here is what you have been missing",
        "Hi {lead_name},\\n\\nIt has been a while since we have heard from you, and a lot has changed!\\n\\n{personalized_hook}\\n\\nHere is what is new since your last visit:\\n\\n🆕 Major platform updates and new capabilities\\n📊 Industry insights: Latest trends in {industry}\\n🏆 Customer success stories from companies like {company}\\n\\nAs a valued contact, we would love to offer you an exclusive:\\n\\n🎁 [Claim your complimentary strategy session →]\\n📄 [See what is new — 2-minute overview →]\\n\\nIf your priorities have shifted, no worries — just click below to update your preferences.\\n\\n[Update my preferences →]\\n\\nBest,\\nThe Team",
      ),

      // 5b. False: Standard winback
      emailNode(
        "5b",
        "Marketo: Send standard winback email for inactive lead",
        1000,
        "{lead_email}",
        "Still interested, {lead_name}? Quick update from us",
        "Hi {lead_name},\\n\\n{personalized_hook}\\n\\nWe have some fresh content that might be relevant to {company}:\\n\\n📄 [Latest industry report for {industry}]\\n🎥 [Quick product demo — 3 minutes]\\n💡 [Top tips from teams like yours]\\n\\nWant to keep hearing from us? No action needed.\\nPrefer to adjust? [Update your preferences →]\\n\\nBest,\\nThe Team",
        380,
      ),

      // 6. Delay: Allow re-engagement time
      n("6", "delayAction", "Wait 14 days for re-engagement", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),

      // 7. Condition: Still inactive?
      n(
        "7",
        "condition",
        "Is the lead still inactive after winback attempt?",
        1480,
        {
          conditionData: [
            {
              variable: "lead_score",
              value: "10",
              comparison_operator: "LESS THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Sunset prompt or stronger offer
      emailNode(
        "8",
        "Marketo: Send sunset prompt or final winback offer",
        1720,
        "{lead_email}",
        "Last check-in: Should we keep sending, {lead_name}?",
        "Hi {lead_name},\\n\\nWe want to respect your inbox. Since we have not heard from you in a while, we want to check — would you still like to receive our emails?\\n\\n👍 [Yes, keep me subscribed — I am just busy]\\n🔄 [Send me less often]\\n👋 [Unsubscribe — no hard feelings]\\n\\nIf we do not hear back, we will reduce your email frequency to quarterly highlights only.\\n\\nWe hope to reconnect when the time is right for {company}.\\n\\nBest,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 123. HIGH-INTENT SALES ALERT (Pattern K — Lead Scoring/Routing)
  // ===================================================================

  "High-Intent Sales Alert": {
    summary:
      "Detects high-intent behavior (pricing page visits, demo page views) and immediately alerts the assigned sales rep with full lead context and SLA tracking. A webhook fires on high-intent page views, Salesforce enriches the lead with fit context, a runCode node computes a composite intent score combining behavior and fit signals, a condition routes above-threshold leads to immediate alert, Slack fires with full lead context for the AE, a delay enforces an SLA window, Salesforce logs the routing decision, and Google Sheets tracks routing quality metrics.",
    nodes: [
      trigger("Webhook: High-intent page viewed in Marketo", "webhook_app", {
        source: "Marketo / Munchkin Tracking",
        event: "page.highintent.viewed",
        howItStarts:
          "A Marketo webhook fires when a known lead visits a high-intent page (pricing, demo request, comparison, or ROI calculator page), indicating strong buying intent that requires immediate sales follow-up",
        setup:
          "Configure Marketo smart campaign to trigger on web page visits to pricing, demo, comparison, and ROI calculator URLs. Set Munchkin tracking on all high-intent pages. Map lead owner for routing.",
        inputFields: [],
      }),

      // 2. Salesforce: Enrich lead with fit context
      salesforceNode(
        "2",
        "Salesforce: Enrich lead with firmographic and fit data",
        280,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Email", customName: "lead_email", type: "string" },
          { name: "FirstName", customName: "lead_name", type: "string" },
          { name: "Company", customName: "company", type: "string" },
          { name: "Title", customName: "job_title", type: "string" },
          {
            name: "NumberOfEmployees",
            customName: "company_size",
            type: "number",
          },
          { name: "Industry", customName: "industry", type: "string" },
          { name: "Lead_Score__c", customName: "lead_score", type: "number" },
          { name: "Owner.Email", customName: "owner_email", type: "string" },
        ],
      ),

      // 3. runCode: Compute composite intent score
      n("3", "runCode", "Compute composite behavior + fit intent score", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute composite intent score from behavior + fit signals
const behaviorScore = parseInt(vars.lead_score) || 0;
const companySize = parseInt(vars.company_size) || 0;
const title = (vars.job_title || "").toLowerCase();

// Fit scoring
let fitScore = 0;
if (companySize > 500) fitScore += 30;
else if (companySize > 100) fitScore += 20;
else if (companySize > 20) fitScore += 10;

// Title scoring
const seniorTitles = ["vp", "director", "head", "chief", "cmo", "cro", "svp"];
const midTitles = ["manager", "lead", "senior"];
if (seniorTitles.some(t => title.includes(t))) fitScore += 25;
else if (midTitles.some(t => title.includes(t))) fitScore += 15;

const compositeScore = behaviorScore + fitScore;
const meetsThreshold = compositeScore >= 60 ? "yes" : "no";
const priority = compositeScore >= 80 ? "P1" : compositeScore >= 60 ? "P2" : "P3";
const slaHours = priority === "P1" ? 2 : priority === "P2" ? 4 : 24;

return {
  composite_score: compositeScore,
  fit_score: fitScore,
  meets_threshold: meetsThreshold,
  priority_level: priority,
  sla_hours: slaHours,
  score_breakdown: "Behavior: " + behaviorScore + " + Fit: " + fitScore + " = " + compositeScore
};`,
          outputVariables: [
            { name: "composite_score", type: "number" },
            { name: "fit_score", type: "number" },
            { name: "meets_threshold", type: "string" },
            { name: "priority_level", type: "string" },
            { name: "sla_hours", type: "number" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 4. Condition: Score above threshold?
      n(
        "4",
        "condition",
        "Does the composite intent score meet the sales threshold?",
        760,
        {
          conditionData: [
            {
              variable: "meets_threshold",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Immediate Slack alert to sales
      slackNode(
        "5",
        "Slack: Immediate high-intent sales alert to AE",
        1000,
        "🚨 *High-Intent Lead Alert — {priority_level}*\n• Lead: {lead_name} ({lead_email})\n• Company: {company} | {industry} | {company_size} employees\n• Title: {job_title}\n• Score: {score_breakdown}\n• Intent: Visited high-intent page (pricing/demo)\n• SLA: Respond within {sla_hours} hours\n• AE: {owner_email}\n• Action: Call or email immediately — this lead is evaluating now",
      ),

      // 6. Delay: SLA enforcement window
      n("6", "delayAction", "Wait 4 hours for SLA compliance check", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),

      // 7. Salesforce: Update score and routing decision
      salesforceNode(
        "7",
        "Salesforce: Update lead score and routing status",
        1480,
        "PATCH",
        "sobjects/Lead/{lead_id}",
        [
          { key: "Lead_Score__c", value: "{composite_score}" },
          { key: "Intent_Priority__c", value: "{priority_level}" },
          { key: "Last_High_Intent_Date__c", value: "{today}" },
          { key: "Routing_Status__c", value: "Alerted" },
        ],
        [{ name: "id", customName: "updated_lead_id", type: "string" }],
      ),

      // 8. Google Sheets: Log routing quality
      sheetsNode(
        "8",
        "Log high-intent routing to quality tracker (Sheets)",
        1720,
        "IntentRouting",
        '[["{lead_email}","{company}","{job_title}","{composite_score}","{priority_level}","{sla_hours}","{owner_email}","{score_breakdown}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 124. SCORE DECAY AND RE-QUALIFICATION (Pattern K — Lead Scoring/Routing)
  // ===================================================================

  "Score Decay and Re-Qualification": {
    summary:
      "Detects leads whose engagement scores have decayed below thresholds and routes them for re-qualification or archive. A webhook fires when a lead score drops below a decay threshold, Salesforce enriches with current lead context, a runCode node computes decay rate and re-qualification eligibility, a condition routes recyclable leads vs. archive candidates, recyclable leads get a Slack alert for SDR re-qualification, a delay allows re-qualification attempts, Salesforce updates the lead status, and Google Sheets logs the decay decision for analysis.",
    nodes: [
      trigger(
        "Webhook: Lead score decayed below threshold in Marketo",
        "webhook_app",
        {
          source: "Marketo / Lead Scoring Engine",
          event: "lead.score.decayed",
          howItStarts:
            "A Marketo webhook fires when a lead score drops below the re-qualification threshold due to time-based decay (no engagement in 60+ days), triggering a review to determine if the lead should be recycled for nurture or archived",
          setup:
            "Configure Marketo lead scoring program with time-based decay rules (e.g., -5 points per 30 days of inactivity). Set threshold triggers at score milestones (e.g., drops below 40 from a previous MQL of 60+). Map decay stages to re-qualification or archive actions.",
          inputFields: [],
        },
      ),

      // 2. Salesforce: Enrich with current lead context
      salesforceNode(
        "2",
        "Salesforce: Lookup decayed lead context and history",
        280,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Email", customName: "lead_email", type: "string" },
          { name: "FirstName", customName: "lead_name", type: "string" },
          { name: "Company", customName: "company", type: "string" },
          { name: "Title", customName: "job_title", type: "string" },
          {
            name: "Lead_Score__c",
            customName: "current_score",
            type: "number",
          },
          { name: "Max_Score__c", customName: "peak_score", type: "number" },
          {
            name: "LastActivityDate",
            customName: "last_activity",
            type: "string",
          },
          { name: "Status", customName: "lead_status", type: "string" },
        ],
      ),

      // 3. runCode: Compute decay rate and re-qualification eligibility
      n(
        "3",
        "runCode",
        "Compute score decay rate and re-qualification eligibility",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Compute decay rate and determine re-qualification vs archive
const currentScore = parseInt(vars.current_score) || 0;
const peakScore = parseInt(vars.peak_score) || 0;
const lastActivity = vars.last_activity || "";
const leadStatus = vars.lead_status || "";

// Calculate decay metrics
const scoreDropPct = peakScore > 0 ? Math.round(((peakScore - currentScore) / peakScore) * 100) : 0;
const daysSinceActivity = lastActivity ? Math.floor((Date.now() - new Date(lastActivity).getTime()) / 86400000) : 999;

// Re-qualification rules
const wasQualified = peakScore >= 60;
const recentEnough = daysSinceActivity < 180;
const goodFit = peakScore >= 40;
const isRecyclable = wasQualified && recentEnough && goodFit;
const decision = isRecyclable ? "recycle" : "archive";
const reason = !wasQualified ? "Never reached MQL threshold" :
  !recentEnough ? "Inactive for 180+ days" :
  !goodFit ? "Low peak score indicates poor fit" : "Was qualified, recent enough to re-engage";

return {
  score_drop_pct: scoreDropPct,
  days_since_activity: daysSinceActivity,
  decay_decision: decision,
  decay_reason: reason,
  was_qualified: wasQualified ? "yes" : "no",
  requalification_priority: isRecyclable && peakScore >= 70 ? "high" : "standard"
};`,
            outputVariables: [
              { name: "score_drop_pct", type: "number" },
              { name: "days_since_activity", type: "number" },
              { name: "decay_decision", type: "string" },
              { name: "decay_reason", type: "string" },
              { name: "was_qualified", type: "string" },
              { name: "requalification_priority", type: "string" },
            ],
          },
        },
      ),

      // 4. Condition: Recycle or archive?
      n(
        "4",
        "condition",
        "Is the lead eligible for re-qualification (recycle)?",
        760,
        {
          conditionData: [
            {
              variable: "decay_decision",
              value: "recycle",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Slack alert for SDR re-qualification
      slackNode(
        "5",
        "Slack: Alert SDR team for lead re-qualification outreach",
        1000,
        "🔄 *Lead Re-Qualification Needed — {requalification_priority} priority*\n• Lead: {lead_name} ({lead_email})\n• Company: {company} | Title: {job_title}\n• Score: {current_score} (peak: {peak_score}, dropped {score_drop_pct}%)\n• Last Activity: {days_since_activity} days ago\n• Status: {lead_status}\n• Reason for recycle: {decay_reason}\n• Action: SDR to attempt re-qualification via phone/email within 48h",
      ),

      // 6. Delay: Allow re-qualification attempts
      n("6", "delayAction", "Wait 5 days for re-qualification attempt", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Salesforce: Update lead status based on decay decision
      salesforceNode(
        "7",
        "Salesforce: Update lead status with decay decision",
        1480,
        "PATCH",
        "sobjects/Lead/{lead_id}",
        [
          { key: "Status", value: "{decay_decision}" },
          { key: "Score_Decay_Date__c", value: "{today}" },
          { key: "Decay_Reason__c", value: "{decay_reason}" },
          { key: "Lead_Score__c", value: "{current_score}" },
        ],
        [{ name: "id", customName: "updated_lead_id", type: "string" }],
      ),

      // 8. Google Sheets: Log decay decision
      sheetsNode(
        "8",
        "Log score decay decision to analytics tracker (Sheets)",
        1720,
        "ScoreDecay",
        '[["{lead_email}","{company}","{current_score}","{peak_score}","{score_drop_pct}","{days_since_activity}","{decay_decision}","{decay_reason}","{requalification_priority}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 125. INTERACTIVE WEBINAR SUMMARY FOLLOW-UP (Pattern I — Content/Event Nurture)
  // ===================================================================

  "Interactive Webinar Summary Follow-Up": {
    summary:
      "Generates an AI-powered personalized summary of an interactive webinar and delivers tailored follow-up based on attendee engagement. A webhook fires when the webinar ends, AI generates a comprehensive summary from the webinar data including poll results and Q&A highlights, Salesforce looks up attendee lifecycle stage, a condition routes engaged attendees vs. passive viewers, the engaged path gets a personalized recap with their specific interactions, a delay allows content consumption, a second condition checks post-webinar engagement, and progressive content or meeting invite completes the nurture.",
    nodes: [
      trigger("Webhook: Interactive webinar ended in Marketo", "webhook_app", {
        source: "Marketo / Zoom Webinars",
        event: "webinar.interactive.ended",
        howItStarts:
          "A Marketo webhook fires when an interactive webinar ends via Zoom integration, containing attendee data, poll responses, Q&A participation, chat activity, and engagement scores for each participant",
        setup:
          "Configure Marketo-Zoom webinar integration. Enable poll tracking, Q&A capture, and attendee engagement scoring in Zoom. Set up Marketo smart campaign to trigger on webinar ended event with attendee-level data.",
        inputFields: [],
      }),

      // 2. AI: Generate webinar summary and personalized highlights
      aiNode(
        "2",
        "OpenAI: Generate AI webinar summary and personalized attendee highlights",
        280,
        "You are a webinar content strategist. Analyze the webinar data and generate a comprehensive summary. Extract attendee_email, attendee_name, company, webinar_title, webinar_topic, key_takeaways (3 bullet points), poll_highlights (summary of poll results), and qa_highlights (top questions asked). Classify engagement_level as active (participated in polls, asked questions, high attention) or passive (attended but minimal interaction). Return JSON with all these fields.",
        "Webinar data: {webhook_payload}",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "webinar_title", as: "webinar_title", type: "string" },
          { field: "webinar_topic", as: "webinar_topic", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "poll_highlights", as: "poll_highlights", type: "string" },
          { field: "qa_highlights", as: "qa_highlights", type: "string" },
          { field: "engagement_level", as: "engagement_level", type: "string" },
        ],
      ),

      // 3. Salesforce: Lookup attendee lifecycle stage
      salesforceNode(
        "3",
        "Salesforce: Lookup attendee lifecycle stage and content history",
        520,
        "GET",
        "sobjects/Lead/{lead_id}",
        [],
        [
          { name: "Status", customName: "lifecycle_stage", type: "string" },
          { name: "Lead_Score__c", customName: "lead_score", type: "number" },
          {
            name: "Webinars_Attended__c",
            customName: "webinars_attended",
            type: "number",
          },
          {
            name: "Last_Content_Topic__c",
            customName: "last_content_topic",
            type: "string",
          },
        ],
      ),

      // 4. Condition: Active participant vs passive viewer
      n(
        "4",
        "condition",
        "Was the attendee actively engaged during the webinar?",
        760,
        {
          conditionData: [
            {
              variable: "engagement_level",
              value: "active",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. True: Personalized recap for active participants
      emailNode(
        "5",
        "Marketo: Send personalized webinar recap for active attendee",
        1000,
        "{attendee_email}",
        '{attendee_name}, your personalized recap of "{webinar_title}"',
        'Hi {attendee_name},\\n\\nThank you for actively participating in "{webinar_title}"! Your engagement made the session richer for everyone.\\n\\n🎯 Key Takeaways:\\n{key_takeaways}\\n\\n📊 Poll Results Highlights:\\n{poll_highlights}\\n\\n❓ Top Q&A Moments:\\n{qa_highlights}\\n\\n🎥 [Watch the full recording →]\\n📄 [Download the presentation slides →]\\n📋 [Get the extended resources guide →]\\n\\nSince you showed strong interest in {webinar_topic}, we have curated some follow-up content specifically for {company}.\\n\\nWant to discuss how these concepts apply to your situation?\\n📞 [Book a 15-min follow-up with a specialist →]\\n\\nBest,\\nThe Webinar Team',
      ),

      // 5b. False: Standard recap for passive viewers
      emailNode(
        "5b",
        "Marketo: Send standard webinar recap for passive viewer",
        1000,
        "{attendee_email}",
        'Your "{webinar_title}" recap and recording',
        'Hi {attendee_name},\\n\\nThanks for joining "{webinar_title}"! Here is everything you need:\\n\\n🎯 Key Takeaways:\\n{key_takeaways}\\n\\n🎥 [Watch the recording →]\\n📄 [Download the slides →]\\n\\nDid not catch everything? The recording covers all the {webinar_topic} insights shared during the session.\\n\\nBest,\\nThe Webinar Team',
        380,
      ),

      // 6. Delay: Allow content consumption
      n(
        "6",
        "delayAction",
        "Wait 4 days for post-webinar content engagement",
        1240,
        {
          delayDetails: { duration: 4, unit: "days" },
        },
      ),

      // 7. Condition: Engaged with follow-up content?
      n(
        "7",
        "condition",
        "Has the attendee engaged with webinar follow-up content?",
        1480,
        {
          conditionData: [
            {
              variable: "webinars_attended",
              value: "2",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Progressive: Deep content or meeting invite
      emailNode(
        "8",
        "Marketo: Send progressive content and consultation invite",
        1720,
        "{attendee_email}",
        "{attendee_name}, take the next step on {webinar_topic}",
        "Hi {attendee_name},\\n\\nSince you have been exploring {webinar_topic} (this is your webinar #{webinars_attended} with us!), here is the next level of content:\\n\\n📄 [In-depth white paper: Advanced {webinar_topic} strategies]\\n📊 [ROI calculator: Quantify the impact for {company}]\\n🎥 [Customer story: How a {lifecycle_stage}-stage company like yours succeeded]\\n\\nReady to move from learning to doing?\\n\\n🗓 [Book a personalized consultation →]\\n\\nOur specialists can help {company} apply these {webinar_topic} concepts to your specific situation.\\n\\nBest,\\nThe Webinar Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },
};
